import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Calendar, Save, Trash2, Eye, Database } from 'lucide-react';
import { useGame } from '../context/GameContext';
import { useAuth } from '../hooks/useAuth';
import { isUserAdmin } from '../utils/admin';
import { 
  getPaginatedPuzzles, 
  seedFirestoreWithPuzzles,
  PaginatedPuzzles
} from '../services/puzzleService';
import { DEFAULT_WORD_CIRCLES } from '../services/wordService';
import { doc, setDoc, deleteDoc, DocumentSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

interface AdminPuzzleModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PuzzleEntry {
  date: string;
  words: string;
  createdAt: Date;
  isNew?: boolean;
}

// Security wrapper component
const AdminPuzzleModal: React.FC<AdminPuzzleModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  
  // Security check - only allow admin users
  const isAdmin = isUserAdmin(user);
  
  if (!isAdmin) {
    console.warn('🚨 Unauthorized access attempt to AdminPuzzleModal by:', user?.email || 'anonymous');
    return null; // Don't render anything for non-admin users
  }

  // Render the actual admin component only for admin users
  return <AdminPuzzleModalComponent isOpen={isOpen} onClose={onClose} />;
};

// The actual admin component (only rendered for admin users)
const AdminPuzzleModalComponent: React.FC<AdminPuzzleModalProps> = ({ isOpen, onClose }) => {
  const { actions } = useGame();
  const [puzzles, setPuzzles] = useState<PuzzleEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);
  const [newPuzzle, setNewPuzzle] = useState<PuzzleEntry>({ date: '', words: '', createdAt: new Date(), isNew: true });

  // Load existing puzzles
  useEffect(() => {
    if (isOpen) {
      loadPuzzles();
    }
  }, [isOpen]);

  const loadPuzzles = async (reset: boolean = true) => {
    if (reset) {
      setLoading(true);
      setPuzzles([]);
      setLastDoc(null);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const result: PaginatedPuzzles = await getPaginatedPuzzles(25, reset ? null : lastDoc);
      const puzzleEntries = result.puzzles.map(puzzle => ({
        date: puzzle.date,
        words: puzzle.words,
        createdAt: puzzle.createdAt
      }));
      
      if (reset) {
        setPuzzles(puzzleEntries);
      } else {
        setPuzzles(prev => [...prev, ...puzzleEntries]);
      }
      
      setHasMore(result.hasMore);
      setLastDoc(result.lastDoc);
    } catch (error) {
      actions.showToast('Failed to load puzzles', 'error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const handleAddPuzzle = async () => {
    if (!newPuzzle.date || !newPuzzle.words.trim()) {
      actions.showToast('Please enter both date and words', 'error');
      return;
    }

    const wordsArray = newPuzzle.words.split(',').map(w => w.trim()).filter(w => w.length > 0);
    if (wordsArray.length < 3) {
      actions.showToast('Please enter at least 3 words', 'error');
      return;
    }

    setSaving(true);
    try {
      await setDoc(doc(db, 'daily_puzzles', newPuzzle.date), {
        date: newPuzzle.date,
        words: wordsArray.join(', '),
        createdAt: new Date(),
      });

      actions.showToast('Puzzle added successfully!', 'success');
      setNewPuzzle({ date: '', words: '', createdAt: new Date(), isNew: true });
      await loadPuzzles(true);
    } catch (error) {
      actions.showToast('Failed to add puzzle', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePuzzle = async (date: string) => {
    if (!confirm(`Are you sure you want to delete the puzzle for ${date}?`)) return;

    try {
      await deleteDoc(doc(db, 'daily_puzzles', date));
      actions.showToast('Puzzle deleted successfully!', 'success');
      await loadPuzzles(true);
    } catch (error) {
      actions.showToast('Failed to delete puzzle', 'error');
    }
  };

  const handleSeedPuzzles = async () => {
    if (!confirm('This will add 7 example puzzles starting from today. Continue?')) return;

    setSaving(true);
    try {
      const success = await seedFirestoreWithPuzzles(7);
      if (success) {
        actions.showToast('Successfully seeded 7 puzzles!', 'success');
        await loadPuzzles(true);
      } else {
        actions.showToast('Failed to seed some puzzles', 'error');
      }
    } catch (error) {
      actions.showToast('Error seeding puzzles', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getExampleWords = (index: number) => {
    const circle = DEFAULT_WORD_CIRCLES[index % DEFAULT_WORD_CIRCLES.length];
    return circle.join(', ');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] shadow-2xl overflow-hidden"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Database className="w-6 h-6" />
                  <h2 className="text-2xl font-bold">Admin: Puzzle Management</h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Quick Actions */}
              <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Database className="w-4 h-4" />
                  Quick Actions
                </h3>
                <button
                  onClick={handleSeedPuzzles}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Seed 7 Example Puzzles
                </button>
              </div>

              {/* Add New Puzzle */}
              <div className="mb-6 p-4 border-2 border-dashed border-purple-200 rounded-xl">
                <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Add New Puzzle
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      Date
                    </label>
                    <input
                      type="date"
                      value={newPuzzle.date}
                      onChange={(e) => setNewPuzzle({ ...newPuzzle, date: e.target.value })}
                      min={getTomorrowDate()}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-600 mb-2">
                      Words (comma-separated)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newPuzzle.words}
                        onChange={(e) => setNewPuzzle({ ...newPuzzle, words: e.target.value })}
                        placeholder="Light, Flame, Burn, Mark, Note, Key, Solution, Dissolve, Melt"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <button
                        onClick={() => setNewPuzzle({ ...newPuzzle, words: getExampleWords(0) })}
                        className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-1"
                        title="Use example words"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={handleAddPuzzle}
                  disabled={saving}
                  className="mt-4 px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Adding...' : 'Add Puzzle'}
                </button>
              </div>

              {/* Existing Puzzles */}
              <div>
                <h3 className="font-bold text-gray-800 mb-4 flex items-center justify-between">
                  <span>Existing Puzzles ({puzzles.length})</span>
                  {puzzles.length > 0 && (
                    <button
                      onClick={() => loadPuzzles(true)}
                      className="px-3 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
                      disabled={loading}
                    >
                      Refresh
                    </button>
                  )}
                </h3>
                
                {loading ? (
                  <div className="text-center py-8 text-gray-500">Loading puzzles...</div>
                ) : puzzles.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No puzzles found. Add some above!</div>
                ) : (
                  <>
                    <div className="grid gap-3 max-h-96 overflow-y-auto">
                      {puzzles.map((puzzle) => {
                        const isToday = puzzle.date === new Date().toISOString().split('T')[0];
                        const isFuture = puzzle.date > new Date().toISOString().split('T')[0];
                        
                        return (
                          <div
                            key={puzzle.date}
                            className={`flex items-center justify-between p-3 rounded-lg hover:bg-gray-100 transition-colors ${
                              isToday ? 'bg-blue-50 border-2 border-blue-200' :
                              isFuture ? 'bg-purple-50 border border-purple-200' : 
                              'bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <Calendar className={`w-4 h-4 ${
                                isToday ? 'text-blue-600' :
                                isFuture ? 'text-purple-600' :
                                'text-gray-400'
                              }`} />
                              <div>
                                <span className={`font-mono text-sm ${
                                  isToday ? 'font-bold text-blue-800' :
                                  isFuture ? 'font-semibold text-purple-800' :
                                  'text-gray-800'
                                }`}>
                                  {puzzle.date}
                                  {isToday && ' (Today)'}
                                  {isFuture && ' (Future)'}
                                </span>
                                <div className="text-xs text-gray-500 truncate max-w-xs">
                                  {puzzle.words}
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleDeletePuzzle(puzzle.date)}
                                className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                                title="Delete puzzle"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {hasMore && (
                      <div className="mt-4 text-center">
                        <button
                          onClick={() => loadPuzzles(false)}
                          disabled={loadingMore}
                          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 rounded-lg transition-colors flex items-center gap-2 mx-auto"
                        >
                          <Plus className="w-4 h-4" />
                          {loadingMore ? 'Loading...' : 'Load More'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AdminPuzzleModal;